import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Gear, Sliders } from 'react-bootstrap-icons';
import { useTheme } from '../../contexts/ThemeContext';
import useIsMobile from '../../hooks/useIsMobile';
import BottomSheet from '../shared/BottomSheet';
import FilterChip from '../shared/FilterChip';
import SegmentedControl from '../shared/SegmentedControl';
import SheetOptionList from '../shared/SheetOptionList';
import SheetSection from '../shared/SheetSection';
import {
  MARKET_OVERVIEW_LINE_STYLE_OPTIONS,
  MARKET_OVERVIEW_LINE_WIDTH_OPTIONS,
  normalizeMarketOverviewChartStyle,
} from '../../constants/marketOverviewChartStyle';

/**
 * Index comparison stroke width / line style: gear popover on desktop,
 * chip + bottom sheet on mobile.
 */
export default function IndexComparisonChartSettings({
  lineWidth,
  lineStyle,
  onChange,
}) {
  const { colors } = useTheme();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const style = normalizeMarketOverviewChartStyle({ lineWidth, lineStyle });

  useEffect(() => {
    // The mobile sheet handles its own dismissal.
    if (!open || isMobile) return undefined;
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, isMobile]);

  const emit = useCallback((patch) => {
    onChange?.(normalizeMarketOverviewChartStyle({ ...style, ...patch }));
  }, [onChange, style]);

  const settingsContent = (
    <>
      <SheetSection title="Stroke">
        <SegmentedControl
          options={MARKET_OVERVIEW_LINE_WIDTH_OPTIONS.map((o) => ({
            value: o.value,
            label: o.label,
            testId: `index-comparison-line-width-${o.value}`,
          }))}
          value={style.lineWidth}
          onChange={(next) => emit({ lineWidth: Number(next) })}
          variant="accent"
          ariaLabel="Stroke thickness"
          testId="index-comparison-line-width"
        />
      </SheetSection>
      <SheetSection title="Line type">
        <SheetOptionList
          options={MARKET_OVERVIEW_LINE_STYLE_OPTIONS}
          value={style.lineStyle}
          onChange={(next) => emit({ lineStyle: Number(next) })}
          ariaLabel="Line type"
          testId="index-comparison-line-style"
          getTestId={(option) => `index-comparison-line-style-${option.value}`}
        />
      </SheetSection>
    </>
  );

  if (isMobile) {
    return (
      <>
        <FilterChip
          testId="index-comparison-settings-button"
          ariaLabel="Index comparison chart settings"
          ariaExpanded={open}
          value="Style"
          leadingIcon={<Sliders size={13} />}
          active={open}
          onClick={() => setOpen(true)}
        />
        <BottomSheet
          show={open}
          onHide={() => setOpen(false)}
          title="Chart style"
          testId="index-comparison-settings-panel"
          closeLabel="Close chart settings"
        >
          {settingsContent}
        </BottomSheet>
      </>
    );
  }

  return (
    <div ref={rootRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        data-testid="index-comparison-settings-button"
        aria-label="Index comparison chart settings"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((prev) => !prev)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          padding: 0,
          borderRadius: 8,
          border: `1px solid ${colors.border}`,
          backgroundColor: open ? `${colors.primary}22` : (colors.bgSecondary || colors.bgCard),
          color: open ? colors.primary : colors.textMuted,
          cursor: 'pointer',
        }}
      >
        <Gear size={14} />
      </button>

      {open ? (
        <div
          data-testid="index-comparison-settings-panel"
          role="dialog"
          aria-label="Index comparison chart settings"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            zIndex: 40,
            width: 280,
            padding: '14px 14px 4px',
            borderRadius: 10,
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.bgCard || colors.bgSecondary,
            boxShadow: '0 10px 28px rgba(0,0,0,0.35)',
          }}
        >
          {settingsContent}
        </div>
      ) : null}
    </div>
  );
}
